import { Link } from "react-router-dom";
import { ArrowRight, Printer, Building2, FileText, ExternalLink, Mail } from "lucide-react";

/**
 * Cover letter for Frost Bank — addressed to Tina Poindexter (VP Community Leader,
 * Corporate Lending) and Michael Cruz (VP – SBA Loan Coordinator).
 *
 * Public route. Linked from /investor/frost-bank (no-gate Plan C view).
 */
export default function FrostBankCoverLetterPage() {
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Build the direct, no-password investor portal link (Plan C is auto-loaded).
  // Uses the production public domain (REACT_APP_PUBLIC_URL) so the printed link
  // never points to an internal preview address.
  const publicBase = (process.env.REACT_APP_PUBLIC_URL || "https://sterilemate.com").replace(/\/$/, "");
  const directLink = `${publicBase}/investor/frost-bank`;

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  // Pre-filled email draft (opens the user's default mail client).
  // Update the two `to` addresses below with the actual Frost Bank inbox addresses.
  const FROST_BANK_RECIPIENTS = [
    "tina.poindexter@frostbank.com",
    "michael.cruz@frostbank.com",
  ];
  const emailSubject =
    "SBA 7(a) Loan Introduction — Sterile Mate LLC · 16651 Ronan Rd, Houston, TX";
  const emailBody = `Dear Ms. Poindexter and Mr. Cruz,

Please find below the direct link to the Sterile Mate LLC SBA 7(a) business plan package for the proposed acquisition of 16651 Ronan Rd, Houston, TX 77060 — a 22,750 SF Class-A 3-story corporate HQ on 5.08 acres.

Cover letter (Frost Bank introduction):
${publicBase}/investor/frost-bank/letter

Plan C business plan (no password required):
${directLink}

Project summary:
• Total project: $4,200,000  ($3.5M acquisition + $20K renovation + $180K statewide B2B marketing launch + $500K operating LOC)
• SBA 7(a) ask: ~$3,700,000  ·  Equity injection: ~$370,000 (10%)
• Floor 2 (12,640 SF) anchor lease — $190K–$228K NNN income from day one
• 2.84-acre platted expansion pad for future Distribution / Medical Device Packaging Center
• 88 on-site parking spaces · Beltway 8 + I-45 · 8 minutes to IAH
• Projected DSCR: Year 1 1.74x → Year 2 2.51x → Year 3 4.62x

I would welcome a short call at your convenience to walk you through the package and provide the supporting documentation needed to begin a formal review.

Respectfully,
George Broughton
Founder & CEO, Sterile Mate LLC
george.broughton@sterilemate.com`;

  const mailtoHref =
    `mailto:${FROST_BANK_RECIPIENTS.join(",")}` +
    `?subject=${encodeURIComponent(emailSubject)}` +
    `&body=${encodeURIComponent(emailBody)}`;

  const gmailHref =
    `https://mail.google.com/mail/?view=cm&fs=1` +
    `&to=${encodeURIComponent(FROST_BANK_RECIPIENTS.join(","))}` +
    `&su=${encodeURIComponent(emailSubject)}` +
    `&body=${encodeURIComponent(emailBody)}`;

  const outlookHref =
    `https://outlook.office.com/mail/deeplink/compose` +
    `?to=${encodeURIComponent(FROST_BANK_RECIPIENTS.join(","))}` +
    `&subject=${encodeURIComponent(emailSubject)}` +
    `&body=${encodeURIComponent(emailBody)}`;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4 print:bg-white print:py-0">
      {/* Toolbar (hidden on print) */}
      <div className="max-w-4xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link
          to="/investor/frost-bank"
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
          data-testid="frost-bank-letter-view-portal"
        >
          <Building2 className="w-4 h-4" />
          View Plan C Business Plan
          <ArrowRight className="w-4 h-4" />
        </Link>

        <div className="flex flex-wrap items-center gap-2">
          <a
            href={mailtoHref}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold"
            data-testid="frost-bank-letter-mailto-btn"
          >
            <Mail className="w-4 h-4" />
            Email to Frost Bank
          </a>
          <a
            href={gmailHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-sm font-semibold"
            data-testid="frost-bank-letter-gmail-btn"
            title="Open in Gmail web"
          >
            Gmail
          </a>
          <a
            href={outlookHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-sm font-semibold"
            data-testid="frost-bank-letter-outlook-btn"
            title="Open in Outlook web"
          >
            Outlook
          </a>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold"
            data-testid="frost-bank-letter-print-btn"
          >
            <Printer className="w-4 h-4" />
            Print / PDF
          </button>
        </div>
      </div>

      {/* Letter page — 8.5x11 feel */}
      <article
        className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg px-12 py-14 text-slate-900 print:shadow-none print:rounded-none print:px-10 print:py-10"
        data-testid="frost-bank-cover-letter"
      >
        {/* Letterhead */}
        <header className="flex items-start justify-between pb-6 border-b-2 border-blue-900">
          <div>
            <h1 className="text-2xl font-bold text-blue-900 leading-tight">Sterile Mate LLC</h1>
            <p className="text-sm text-slate-600 mt-1">Healthcare Solutions · Powered by AskIsaac</p>
            <p className="text-xs text-slate-500 mt-2">
              Houston, TX · george.broughton@sterilemate.com · sterilemate.com
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-500">Confidential</p>
            <p className="text-sm text-slate-700 mt-1">{today}</p>
          </div>
        </header>

        {/* Recipient block */}
        <section className="mt-8 text-sm leading-6">
          <p className="font-semibold text-slate-900">Tina Poindexter</p>
          <p className="text-slate-700">Vice President · Community Leader | Corporate Lending</p>
          <p className="text-slate-700">Frost Bank</p>

          <p className="font-semibold text-slate-900 mt-4">Michael Cruz</p>
          <p className="text-slate-700">Vice President · SBA Loan Coordinator</p>
          <p className="text-slate-700">Frost Bank</p>
        </section>

        <p className="mt-8 text-sm">
          <span className="font-semibold">RE:&nbsp;</span>
          SBA 7(a) Loan Introduction — Acquisition of 16651 Ronan Rd, Houston, TX 77060 ·
          22,750 SF Class-A 3-Story Corporate HQ on 5.08 Acres · Total Project $4,200,000
        </p>

        <p className="mt-6 text-[15px]">Dear Ms. Poindexter and Mr. Cruz,</p>

        {/* Body */}
        <section className="mt-5 space-y-5 text-[15px] leading-7 text-slate-800 text-justify">
          <p>
            Thank you for taking the time to consider Sterile Mate LLC for an SBA 7(a)
            financing relationship with Frost Bank. I am writing to formally introduce
            our company and the owner-occupied real estate opportunity we have selected
            for our next phase of growth: the acquisition of{" "}
            <span className="font-semibold">16651 Ronan Rd, Houston, TX 77060</span> — a
            22,750 SF Class-A 3-story corporate headquarters situated on 5.08 acres at
            the intersection of Beltway 8 and I-45, in the Greenspoint corporate
            corridor.
          </p>

          <p>
            Sterile Mate LLC, established in 2020, is a Houston-based, HUB-certified
            healthcare solutions provider serving hospitals, surgery centers, and
            outpatient facilities across Texas. Over the last six years we have
            generated{" "}
            <span className="font-semibold">$3.9M+ in cumulative revenue</span>, are an
            active vendor to the{" "}
            <span className="font-semibold">Harris Health System</span> (with $1.182M of
            confirmed water-purification and AAMI ST108-compliance work currently in
            progress), and operate an in-house AI platform —{" "}
            <span className="font-semibold">AskIsaac</span> — that supports our daily
            sales, project management, and field operations.
          </p>

          <p className="font-semibold text-blue-900">
            Proposed SBA 7(a) Capital Stack — Total Project $4,200,000
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <span className="font-semibold">$3,500,000</span> &mdash; Building &amp; land
              acquisition (22,750 SF Class-A HQ on 5.08 acres)
            </li>
            <li>
              <span className="font-semibold">$20,000</span> &mdash; Renovation &amp; capital
              improvements ($10K accredited architectural-firm proposal + $10K
              contingency &mdash; the building is move-in-ready Class-A)
            </li>
            <li>
              <span className="font-semibold">$180,000</span> &mdash; Statewide B2B marketing
              launch (digital advertising, medical-expo sponsorships, and trade-show
              participation across Texas &mdash; Sterile Mate&rsquo;s first dedicated
              marketing budget driving pipeline ROI)
            </li>
            <li>
              <span className="font-semibold">$500,000</span> &mdash; Operating capital,
              structured as a <span className="font-semibold">separate revolving
              line of credit</span> so the SBA 7(a) facility funds only the real estate,
              renovation, and marketing launch
            </li>
            <li>
              <span className="font-semibold">~$370,000</span> &mdash; Borrower equity
              injection (10% of the $3.7M SBA-financed portion)
            </li>
            <li>
              <span className="font-semibold">~$31,500 / month</span> P&amp;I (25-year
              amortization at Prime + 2.75%) · projected DSCR Year 1{" "}
              <span className="font-semibold">1.74x</span> → Year 2{" "}
              <span className="font-semibold">2.51x</span> → Year 3{" "}
              <span className="font-semibold">4.62x</span>
            </li>
          </ul>

          <p className="font-semibold text-blue-900">Why 16651 Ronan Rd De-Risks This Loan</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>
              <span className="font-semibold">Guaranteed Floor 2 anchor lease income.</span>{" "}
              An established Greenspoint salon-suite operator who already runs two leased
              floors in a nearby high-rise has agreed to anchor the entire 12,640 SF
              Floor 2 under a 7-year triple-net (NNN) lease at $15–$18/SF —{" "}
              <span className="font-semibold">$190,000–$228,000 of annual rental income
              from day one</span>, no lease-up risk, layered directly on top of Sterile
              Mate&rsquo;s operating profit.
            </li>
            <li>
              <span className="font-semibold">
                2.84-acre platted expansion pad for a future Distribution Center /
                Medical Device Packaging Center.
              </span>{" "}
              As our B2B logistics and Online DME retail fulfillment scale, we can build
              on land we already own — eliminating the need to identify, lease, or
              acquire a separate warehouse property. Every dollar of future expansion
              CapEx compounds back into the SBA-collateralized parcel, strengthening
              Frost Bank&rsquo;s loan-to-value position.
            </li>
            <li>
              <span className="font-semibold">88 available on-site parking spaces</span>{" "}
              serving HQ + Floor 2 tenant, with an additional 2.23-acre flex/fleet-parking
              tract on the same title.
            </li>
            <li>
              <span className="font-semibold">200,000+ daily vehicles</span> at the
              Beltway 8 + I-45 intersection · 1.5 miles from the ExxonMobil headquarters
              campus · 8 minutes to George Bush Intercontinental Airport (IAH) for
              direct DME shipping.
            </li>
            <li>
              <span className="font-semibold">Move-in-ready Class-A asset</span> —
              brick exterior, executive lobby, top-floor executive suite + boardroom,
              wellness room, lunch room, two stairwells + elevator (ADA accessible) —
              minimal demo and only ~$200K renovation budget required.
            </li>
          </ul>

          <p>
            To make your initial review as straightforward as possible, we have prepared
            a complete digital business plan package for Plan C (16651 Ronan Rd),
            including the SBA 7(a) executive summary, full property detail, 3-year
            financial projections, capital-stack breakdown, risk &amp; mitigation
            tables, and downloadable Pro-Forma (XLSX) and Investor Package (PDF).
          </p>

          <div className="my-6 rounded-lg border-2 border-blue-700 bg-blue-50 px-6 py-5">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-900">
              Direct Access — No Password Required
            </p>
            <p className="mt-2 text-sm text-slate-800">
              The link below is a dedicated, no-login access point prepared specifically
              for Frost Bank. It opens the Plan C business plan directly:
            </p>
            <a
              href={directLink}
              className="mt-3 inline-flex items-center gap-2 text-blue-800 font-mono text-sm break-all underline underline-offset-4"
              data-testid="frost-bank-direct-link"
            >
              <ExternalLink className="w-4 h-4 shrink-0" />
              {directLink}
            </a>
            <p className="mt-3 text-xs text-slate-600">
              Inside, you will find a &ldquo;Plan C PDF&rdquo; download button (full SBA
              business plan), a &ldquo;Pro-Forma XLSX&rdquo; download, the as-built
              floor-plan PDF, and the DEALCO listing brochure for the property.
            </p>
          </div>

          <p>
            I would welcome the opportunity to walk you both through the package on a
            short call, answer any underwriting questions, and provide the supporting
            documentation Frost Bank will need to begin a formal review. I have prepared
            personal financial statements, business tax returns (2020–2024), interim
            P&amp;L statements, the Harris Health contracts &amp; pipeline backup, and
            the proposed Floor 2 lease term sheet, all of which I can submit at your
            convenience.
          </p>

          <p>
            Thank you again for considering Sterile Mate LLC. Frost Bank&rsquo;s
            longstanding commitment to Texas-based, owner-operated businesses, and your
            SBA 7(a) execution track record, make Frost our first-choice lender for this
            project. I look forward to the opportunity to earn your partnership.
          </p>
        </section>

        {/* Signature */}
        <section className="mt-10 text-[15px]">
          <p>Respectfully,</p>
          <p className="mt-12 font-semibold text-slate-900">George Broughton</p>
          <p className="text-slate-700">Founder &amp; Chief Executive Officer</p>
          <p className="text-slate-700">Sterile Mate LLC</p>
          <p className="text-slate-700 mt-1">george.broughton@sterilemate.com</p>
        </section>

        {/* Enclosures */}
        <section className="mt-10 pt-5 border-t border-slate-300 text-[13px] text-slate-700">
          <p className="font-semibold text-slate-900">Enclosed (Digital Portal):</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>SBA 7(a) Business Plan — Plan C (PDF)</li>
            <li>Plan C 3-Year Financial Projections (PDF + XLSX)</li>
            <li>Plan C Pro-Forma Worksheet (XLSX)</li>
            <li>16651 Ronan Rd DEALCO Listing Brochure (PDF)</li>
            <li>16651 Ronan Rd As-Built Floor Plans (PDF)</li>
          </ul>
        </section>

        {/* About the Founder — closes the diligence loop without forcing the
            reviewer to navigate. Portrait + 2-line tease + link to the full
            executive CV. Prints cleanly with the rest of the letter. */}
        <section className="mt-10 pt-5 border-t border-slate-300" data-testid="frost-about-founder">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 mb-3">About the Founder</p>
          <div className="flex items-start gap-4">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/george_broughton_portrait.jpg`}
              alt="George Broughton III"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border border-slate-300 shadow-sm shrink-0"
            />
            <div className="flex-1">
              <p className="text-[14px] font-semibold text-slate-900">George Broughton III</p>
              <p className="text-[12.5px] text-slate-600 mb-2">
                Founder &amp; CEO, Sterile Mate LLC · Healthcare supply chain executive · AAMI ST108 SME · Architect of AskIsaac.io
              </p>
              <p className="text-[13px] text-slate-700 leading-relaxed mb-3">
                Houston-based, MBE-certified medical device distributor and healthcare facilities infrastructure operator. Active hospital-system vendor since October 2021. Currently delivering critical-water (RO/DI polisher) systems across multiple Houston-area hospital campuses, with additional capital-project installs in motion via contractor-led referrals.
              </p>
              <a
                href="https://www.linkedin.com/in/georgebroughtoniii"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#0056A8] hover:text-[#003366] print:text-slate-900"
                data-testid="frost-linkedin-link"
              >
                Connect on LinkedIn →
              </a>
              <span className="hidden print:inline text-[11px] text-slate-500 ml-1">
                (linkedin.com/in/georgebroughtoniii)
              </span>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-10 pt-5 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
          <span>Sterile Mate LLC · Confidential — for Frost Bank review only</span>
          <span className="inline-flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Cover letter generated {today}
          </span>
        </footer>
      </article>

      {/* CTA below the letter (hidden on print) */}
      <div className="max-w-4xl mx-auto mt-6 print:hidden">
        <Link
          to="/investor/frost-bank"
          className="block w-full text-center rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-bold py-4 text-base"
          data-testid="frost-bank-letter-open-portal-cta"
        >
          Open the Plan C Business Plan &rarr;
        </Link>
      </div>
    </div>
  );
}
